// ================================================================
// src/freeStudy/purchase.js
// ارکستراسیون خرید + gateway abstraction
// ================================================================
import {
  TX_TYPES,
  computeVideoPrice,
  computeBundlePrice,
  getUnownedVideos,
  isEffectivelyFree,
  isUnlocked,
  recomputeDerived,
} from './core.js';
import {
  getPurchaseState,
  mutatePurchaseState,
  generateIdempotencyKey,
  findTransactionByIdempotencyKey,
} from './storage.js';
// 🆕 پل به درگاه واقعی زرین‌پال
import {
  startPayment,
  verifyPayment,
  PAYMENT_PURPOSE,
} from '../services/paymentService.js';


const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ================================================================
// A) Payment Gateway — Mock (قابل تعویض با real)
// ================================================================
export const paymentGateway = {
  name: 'mock',

  /**
   * شبیه‌سازی پرداخت
   * در نسخه‌ی واقعی: زرین‌پال / آیدی‌پی / ...
   */
  async pay({ amount, currency = 'IRT', description = '' }) {
    // شبیه‌سازی تأخیر شبکه
    await new Promise((r) => setTimeout(r, 900));

    if (!amount || amount <= 0) {
      return { success: false, error: 'مبلغ نامعتبر است' };
    }

    // 95% موفقیت (برای تست)
    const ok = Math.random() > 0.05;

    if (!ok) {
      return {
        success: false,
        error: 'پرداخت توسط درگاه رد شد. لطفاً دوباره تلاش کنید.',
      };
    }

    return {
      success: true,
      gateway: 'mock',
      gatewayRef: `MOCK-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)
        .toUpperCase()}`,
      paidAt: Date.now(),
      currency,
      amount,
      description,
    };
  },
};

// ================================================================
// B) Helpers
// ================================================================
function buildTxId() {
  return `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function successResult(state, tx, message) {
  return {
    ok: true,
    message,
    transaction: tx,
    state,
  };
}

function failResult(message) {
  return { ok: false, message };
}














// ================================================================
// 🆕 Pending Purchase Storage — Server-backed
// برای ذخیره‌ی context قبل از redirect به درگاه
// ================================================================
export async function savePendingPurchase(userId, data) {
  if (!userId) return;
  const payload = { ...data, userId, startedAt: data.startedAt || Date.now() };
  try {
    // اول رکورد قبلی را پاک کن (که فقط یک pending برای هر کاربر باشد)
    const check = await fetch(
      `${API_BASE}/freeStudyPending?userId=${encodeURIComponent(userId)}`
    );
    const arr = await check.json();
    if (Array.isArray(arr)) {
      await Promise.all(
        arr.map((rec) =>
          fetch(`${API_BASE}/freeStudyPending/${rec.id}`, { method: 'DELETE' }).catch(() => {})
        )
      );
    }
    // رکورد جدید بساز
    await fetch(`${API_BASE}/freeStudyPending`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    console.warn('⚠️ savePendingPurchase failed:', e);
  }
}

export async function getPendingPurchase(userId) {
  if (!userId) return null;
  try {
    const res = await fetch(
      `${API_BASE}/freeStudyPending?userId=${encodeURIComponent(userId)}`
    );
    const data = await res.json();
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch (e) {
    console.warn('⚠️ getPendingPurchase failed:', e);
    return null;
  }
}

export async function clearPendingPurchase(userId) {
  if (!userId) return;
  try {
    const res = await fetch(
      `${API_BASE}/freeStudyPending?userId=${encodeURIComponent(userId)}`
    );
    const arr = await res.json();
    if (Array.isArray(arr)) {
      await Promise.all(
        arr.map((rec) =>
          fetch(`${API_BASE}/freeStudyPending/${rec.id}`, { method: 'DELETE' }).catch(() => {})
        )
      );
    }
  } catch (e) {
    console.warn('⚠️ clearPendingPurchase failed:', e);
  }
}

export async function hasPendingPurchase(userId) {
  return Boolean(await getPendingPurchase(userId));
}















export async function purchaseVideo({
  userId,
  course,
  videoId,
  idempotencyKey,
}) {
  if (!userId) return failResult('برای خرید ابتدا وارد شوید');
  if (!course) return failResult('دوره یافت نشد');

  const video = course.videos.find((v) => v.id === videoId);
  if (!video) return failResult('ویدیو یافت نشد');

  const state = await getPurchaseState(userId, course.id);

  if (isUnlocked(video, state.unlockedVideoIds)) {
    return successResult(state, null, 'این ویدیو قبلاً برای شما باز شده است');
  }

  if (isEffectivelyFree(video)) {
    return grantFreeVideos({
      userId,
      course,
      videoIds: [video.id],
      reason: 'free-video',
    });
  }

  const amount = computeVideoPrice(video);
  const key = idempotencyKey || generateIdempotencyKey();

  const existing = findTransactionByIdempotencyKey(state.transactions, key);
  if (existing) {
    return successResult(state, existing, 'این تراکنش قبلاً انجام شده است');
  }

  // 🆕 ذخیره‌ی context قبل از رفتن به درگاه (بعد از بازگشت ازش استفاده می‌کنیم)
  await savePendingPurchase(userId, {
    type: TX_TYPES.PURCHASE_VIDEO,
    courseId: course.id,
    videoIds: [video.id],
    amount,
    currency: course.currency || 'IRT',
    idempotencyKey: key,
    courseFullPriceAtPurchase: course?.pricing?.fullCoursePrice || 0,
    title: video.title,
    startedAt: Date.now(),
  });

  // 🆕 هدایت به درگاه واقعی — این خط کاربر را از صفحه خارج می‌کند
  try {
    await startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.FREE_STUDY,
      metadata: {
        courseId: course.id,
        videoId: video.id,
        bundle: false,
        kind: 'free_study_video',
        amountToman: amount,
      },
      description: `خرید ویدیو: ${video.title}`,
    });
  } catch (e) {
    await clearPendingPurchase(userId);
    return failResult(e.message || 'خطا در اتصال به درگاه پرداخت');
  }

  // این خط عملاً اجرا نمی‌شود چون startPayment کاربر را redirect کرده
  return { ok: true, pending: true, message: 'در حال انتقال به درگاه پرداخت...' };
}

// ================================================================
// D) Purchase Bundle — خرید کل دوره
// ================================================================
export async function purchaseBundle({
  userId,
  course,
  idempotencyKey,
}) {
  if (!userId) return failResult('برای خرید ابتدا وارد شوید');
  if (!course) return failResult('دوره یافت نشد');

  const state = await getPurchaseState(userId, course.id);
  const unowned = getUnownedVideos(course, state.unlockedVideoIds);

  if (unowned.length === 0) {
    return successResult(state, null, 'همه‌ی ویدیوهای این دوره برای شما باز است');
  }

  const amount = computeBundlePrice(course, state);

  if (amount <= 0) {
    return grantFreeVideos({
      userId,
      course,
      videoIds: unowned.map((v) => v.id),
      reason: 'bundle-free-after-partial',
    });
  }

  const key = idempotencyKey || generateIdempotencyKey();
  const existing = findTransactionByIdempotencyKey(state.transactions, key);
  if (existing) {
    return successResult(state, existing, 'این تراکنش قبلاً انجام شده است');
  }

  // 🆕 ذخیره‌ی context قبل از redirect
  await savePendingPurchase(userId, {
    type: TX_TYPES.PURCHASE_BUNDLE,
    courseId: course.id,
    videoIds: unowned.map((v) => v.id),
    amount,
    currency: course.currency || 'IRT',
    idempotencyKey: key,
    courseFullPriceAtPurchase: course?.pricing?.fullCoursePrice || 0,
    unownedCountAtPurchase: unowned.length,
    title: course.title,
    startedAt: Date.now(),
  });

  // 🆕 هدایت به درگاه واقعی
  try {
    await startPayment({
      userId,
      purpose: PAYMENT_PURPOSE.FREE_STUDY,
      metadata: {
        courseId: course.id,
        bundle: true,
        kind: 'free_study_bundle',
        amountToman: amount,
      },
      description: `خرید بسته‌ی کل دوره: ${course.title}`,
    });
  } catch (e) {
    await clearPendingPurchase(userId);
    return failResult(e.message || 'خطا در اتصال به درگاه پرداخت');
  }

  return { ok: true, pending: true, message: 'در حال انتقال به درگاه پرداخت...' };
}

// ================================================================
// E) Grant Free — باز کردن رایگان (بدون gateway)
// ================================================================
export async function grantFreeVideos({
  userId,
  course,
  videoIds,
  reason = 'free',
}) {
  if (!userId || !course || !videoIds?.length) {
    return failResult('پارامترهای نامعتبر');
  }

  const tx = {
    id: buildTxId(),
    idempotencyKey: generateIdempotencyKey(),
    type: TX_TYPES.GRANT,
    amount: 0,
    currency: course.currency || 'IRT',
    videoIds,
    ts: Date.now(),
    status: 'success',
    gateway: 'system',
    gatewayRef: `GRANT-${Date.now()}`,
    meta: { reason },
  };

  const next = await mutatePurchaseState(userId, course.id, (cur) => {
    const transactions = [...(cur.transactions || []), tx];
    const derived = recomputeDerived(transactions);
    return { ...cur, transactions, ...derived };
  });

  return successResult(next, tx, 'ویدیوهای رایگان باز شدند');
}

// ================================================================
// F) Refund — بازگرداندن (اختیاری، برای آینده)
// ================================================================
export async function refundPurchase({ userId, course, transactionId }) {
  if (!userId || !course || !transactionId) {
    return failResult('پارامترهای نامعتبر');
  }

  const state = getPurchaseState(userId, course.id);
  const original = state.transactions.find((t) => t.id === transactionId);

  if (!original) return failResult('تراکنش یافت نشد');
  if (original.type === TX_TYPES.REFUND)
    return failResult('این تراکنش قابل بازگشت نیست');

  const tx = {
    id: buildTxId(),
    idempotencyKey: generateIdempotencyKey(),
    type: TX_TYPES.REFUND,
    amount: original.amount,
    currency: original.currency,
    videoIds: original.videoIds,
    ts: Date.now(),
    status: 'success',
    gateway: 'system',
    gatewayRef: `REFUND-${original.id}`,
    meta: { originalTxId: original.id },
  };

  const next = await mutatePurchaseState(userId, course.id, (cur) => {
    const transactions = [...(cur.transactions || []), tx];
    const derived = recomputeDerived(transactions);
    return { ...cur, transactions, ...derived };
  });

  return successResult(next, tx, 'بازگشت وجه ثبت شد');
}














// ================================================================
// 🆕 G) Complete Pending Purchase — بعد از بازگشت از درگاه
// این تابع از PaymentReturn صدا زده می‌شود.
// ================================================================
export async function completePendingPurchase({ userId, callback }) {
  if (!userId) return failResult('کاربر نامعتبر');

  const pending = await getPendingPurchase(userId);
  if (!pending) {
    return failResult('خرید در انتظاری برای این کاربر یافت نشد');
  }

  // اگر مبلغ صفر بود یا بدون درگاه، مستقیم grant کن
  // (این حالت الان اتفاق نمی‌افتد چون purchase* قبل از redirect این‌ها را رد می‌کند)

  // ۱) تأیید با سرور (سرور با زرین‌پال صحبت می‌کند)
  let verifyResult;
  try {
    verifyResult = await verifyPayment({
      paymentId: callback.paymentId,
      authority: callback.authority,
      status: callback.status,
    });
  } catch (e) {
    await clearPendingPurchase(userId);
    return failResult(e.message || 'خطای شبکه در تأیید پرداخت');
  }

  if (!verifyResult?.success) {
    await clearPendingPurchase(userId);
    return failResult(
      verifyResult?.message || 'پرداخت توسط درگاه تأیید نشد'
    );
  }

  // ۲) ساخت تراکنش محلی از روی pending + نتیجه‌ی verify
  const tx = {
    id: buildTxId(),
    idempotencyKey: pending.idempotencyKey,
    type: pending.type,
    amount: pending.amount,
    currency: pending.currency || 'IRT',
    videoIds: pending.videoIds,
    ts: Date.now(),
    status: 'success',
    gateway: 'zarinpal',
    gatewayRef: verifyResult.refId,
    meta: {
      courseFullPriceAtPurchase: pending.courseFullPriceAtPurchase,
      unownedCountAtPurchase: pending.unownedCountAtPurchase,
      paymentId: verifyResult.paymentId,
      cardPan: verifyResult.cardPan || null,
    },
  };

  // ۳) ذخیره‌ی اتمیک در purchase state
  const next = await mutatePurchaseState(
    userId,
    pending.courseId,
    (cur) => {
      // اگر قبلاً ثبت شده، دوباره ثبت نکن (idempotent)
      const already = (cur.transactions || []).find(
        (t) => t.idempotencyKey === pending.idempotencyKey
      );
      if (already) return cur;

      const transactions = [...(cur.transactions || []), tx];
      const derived = recomputeDerived(transactions);
      return { ...cur, transactions, ...derived };
    }
  );

  await clearPendingPurchase(userId);

  return {
    ...successResult(next, tx, 'پرداخت موفق — دسترسی باز شد'),
    courseId: pending.courseId,
    refId: verifyResult.refId,
    paymentId: verifyResult.paymentId,
  };
}
